import {
  ActivityItem,
  Card,
  CardHeader,
  MetricCard,
  PageColumns,
  PageHeading,
  StatusBadge,
} from "@peer-start/ui";
import { Activity, MessageSquareText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../../../app/use-app-context.js";
import { usePassportBootstrap } from "../api/queries.js";
import { LegacyHistory } from "../components/LegacyHistory.js";
import { PassportQueryState } from "../components/PassportQueryState.js";
import { RouteOverviewProgress } from "../components/RouteOverviewProgress.js";

export function PassportActivityPage() {
  const { t, i18n } = useTranslation();
  const { persona } = useAppContext();
  const query = usePassportBootstrap(persona.kind === "employee");
  if (persona.kind !== "employee") return null;
  const overview = query.data?.employeeOverview;
  const events = query.data?.events.filter((event) => event.employeeId === persona.id) ?? [];
  return (
    <>
      <PageHeading title={t("passportVig.activity.title")} subtitle={t("passportVig.activity.subtitle")} />
      <PassportQueryState
        error={query.isError}
        loading={query.isLoading}
        onRetry={() => void query.refetch()}
      >
        <PageColumns
          aside={
            <>
              <MetricCard
                label={t("passportVig.activity.counter")}
                value={events.length}
                icon={<Activity className="size-5" />}
              />
              {overview ? <RouteOverviewProgress overview={overview} /> : null}
            </>
          }
        >
          <Card aria-label={t("passportVig.activity.currentHistory")}>
            <CardHeader title={t("passportVig.activity.currentHistory")} />
            {events.length ? (
              <ol className="divide-y divide-border/60">
                {events.map((event) => (
                  <ActivityItem
                    key={event.id}
                    title={event.summary}
                    description={[
                      query.data?.catalogue.pages.find((page) => page.id === event.pageId)?.title,
                      query.data?.catalogue.stamps.find((stamp) => stamp.id === event.stampId)?.shortTitle,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    timestamp={event.timestamp}
                    dateLabel={new Intl.DateTimeFormat(i18n.language, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    }).format(new Date(event.timestamp))}
                    icon={
                      event.feedback ? (
                        <MessageSquareText className="size-5" />
                      ) : (
                        <Activity className="size-5" />
                      )
                    }
                    feedback={event.feedback}
                    status={
                      event.resultingStatus ? (
                        <StatusBadge
                          compact
                          label={t(`status.${event.resultingStatus}`)}
                          status={event.resultingStatus}
                        />
                      ) : null
                    }
                  />
                ))}
              </ol>
            ) : (
              <p className="py-8 text-center text-sm text-muted-foreground">{t("activity.empty")}</p>
            )}
          </Card>
          <LegacyHistory items={query.data?.legacyHistory ?? []} />
        </PageColumns>
      </PassportQueryState>
    </>
  );
}
